import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { church, formattedAddress } from '../data/church.js';
import verses from '../data/verses.json';
import './Footer.css';

const VERSE_INTERVAL_MS = 9000;
const FADE_MS = 600;

/** Simple inline glyphs — one consistent stroke weight, per the design system. */
const socialIcons = {
  Facebook: 'M13.5 8.5V6.8c0-.6.4-.8.7-.8h1.8V3.3h-2.5c-2.4 0-3 1.8-3 3v2.2H9v2.8h1.5V18h3v-6.7h2l.3-2.8h-2.3z',
  Twitter: 'M17.3 5.6c-.6.3-1.2.5-1.9.6.7-.4 1.2-1 1.4-1.8-.6.4-1.3.7-2.1.8a3.3 3.3 0 0 0-5.6 3C6.4 8 4 6.7 2.4 4.7a3.3 3.3 0 0 0 1 4.4c-.5 0-1-.2-1.5-.4 0 1.6 1.1 2.9 2.6 3.2-.5.2-1 .2-1.5.1.4 1.3 1.6 2.3 3.1 2.3A6.6 6.6 0 0 1 1 15.7 9.3 9.3 0 0 0 6 17.2c6 0 9.4-5 9.4-9.4v-.4c.6-.5 1.2-1.1 1.6-1.8z',
  Instagram: 'M10 5.4c1.5 0 1.7 0 2.3.03 1.6.07 2.3.8 2.4 2.4.03.6.03.8.03 2.2s0 1.6-.03 2.2c-.07 1.6-.8 2.3-2.4 2.4-.6.03-.8.03-2.3.03s-1.7 0-2.3-.03c-1.6-.07-2.3-.8-2.4-2.4C5.3 11.6 5.3 11.4 5.3 10s0-1.6.03-2.2c.07-1.6.8-2.3 2.4-2.4.6-.03.8-.03 2.3-.03zM10 4c-1.5 0-1.7 0-2.3.04-2.2.1-3.4 1.3-3.5 3.5C4.1 8.2 4.1 8.4 4.1 10s0 1.8.04 2.4c.1 2.2 1.3 3.4 3.5 3.5.6.04.8.04 2.4.04s1.8 0 2.4-.04c2.2-.1 3.4-1.3 3.5-3.5.04-.6.04-.8.04-2.4s0-1.8-.04-2.4c-.1-2.2-1.3-3.4-3.5-3.5C11.8 4 11.6 4 10 4zm0 2.9a3.1 3.1 0 1 0 0 6.2 3.1 3.1 0 0 0 0-6.2zm0 5.1a2 2 0 1 1 0-4 2 2 0 0 1 0 4zm3.2-5.2a.73.73 0 1 0 0-1.46.73.73 0 0 0 0 1.46z',
  YouTube: 'M17.4 6.6a1.9 1.9 0 0 0-1.4-1.4C14.8 5 10 5 10 5s-4.8 0-6 .3A1.9 1.9 0 0 0 2.6 6.6C2.3 7.8 2.3 10 2.3 10s0 2.2.3 3.4c.2.7.7 1.2 1.4 1.4 1.2.3 6 .3 6 .3s4.8 0 6-.3a1.9 1.9 0 0 0 1.4-1.4c.3-1.2.3-3.4.3-3.4s0-2.2-.3-3.4zM8.5 12.3V7.7l4 2.3-4 2.3z',
};

/**
 * Rotating scripture. Fades out, swaps text mid-fade, fades back in — so the
 * two verses never overlap visually.
 */
function RotatingVerse() {
  const [index, setIndex] = useState(() => Math.floor(Math.random() * verses.length));
  const [fading, setFading] = useState(false);
  const timers = useRef([]);

  useEffect(() => {
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

    const id = window.setInterval(() => {
      if (reduced) {
        setIndex((i) => (i + 1) % verses.length);
        return;
      }

      setFading(true);
      const swap = window.setTimeout(() => {
        setIndex((i) => (i + 1) % verses.length);
        setFading(false);
      }, FADE_MS);
      timers.current.push(swap);
    }, VERSE_INTERVAL_MS);

    return () => {
      window.clearInterval(id);
      timers.current.forEach(window.clearTimeout);
      timers.current = [];
    };
  }, []);

  const verse = verses[index];

  return (
    <div className="footer__verse">
      {/* aria-live is deliberately off: an unprompted scripture rotation
          interrupting a screen reader mid-sentence would be hostile. The
          current verse is still fully readable in document order. */}
      <blockquote className="footer__verse-text" data-fading={fading}>
        &ldquo;{verse.text}&rdquo;
        <cite className="footer__verse-ref">{verse.reference}</cite>
      </blockquote>
    </div>
  );
}

export default function Footer() {
  const directionsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
    church.mapQuery
  )}`;

  return (
    <footer className="footer on-dark">
      <div className="container">
        <div className="footer__grid">
          {/* --- 1: Logo --- */}
          <div className="footer__col footer__logo">
            <Link to="/" aria-label={`${church.name} — home`}>
              <img src="/assets/met_logo_lrg.png" alt={church.name} />
            </Link>
            <p className="footer__tagline">
              Committed to God, to fellowship, and to service in Metropolitan
              Detroit since 1954.
            </p>
          </div>

          {/* --- 2: Address & contact --- */}
          <div className="footer__col">
            <h2 className="footer__heading">Visit &amp; Contact</h2>

            <address className="footer__address">
              {church.address.street}
              <br />
              {church.address.city}, {church.address.state} {church.address.zip}
              <br />
              <a href={church.phoneHref}>{church.phone}</a>
              <br />
              <a href={`mailto:${church.email}`}>{church.email}</a>
            </address>

            <a
              className="footer__directions"
              href={directionsUrl}
              target="_blank"
              rel="noreferrer"
            >
              Get Directions
              <span className="sr-only">(opens in a new tab)</span>
              <svg width="14" height="10" viewBox="0 0 14 10" fill="none" aria-hidden="true">
                <path
                  d="M1 5h11M8.5 1L12.5 5L8.5 9"
                  stroke="currentColor"
                  strokeWidth="1.75"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </a>

            <ul className="footer__social">
              {church.social.map((s) => (
                <li key={s.name}>
                  <a href={s.href} target="_blank" rel="noreferrer" aria-label={`${church.shortName} on ${s.name}`}>
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                      <path d={socialIcons[s.name]} />
                    </svg>
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* --- 3: Intentionally empty --- */}
          <div className="footer__col footer__col--spacer" aria-hidden="true" />

          {/* --- 4: Rotating scripture --- */}
          <div className="footer__col">
            <h2 className="footer__heading">A Word for Today</h2>
            <RotatingVerse />
          </div>
        </div>

        <div className="footer__bottom">
          <p>
            &copy; {new Date().getFullYear()} {church.name}. All rights reserved.
          </p>

          <ul className="footer__bottom-links">
            <li>
              <Link to="/about/what-we-believe">What We Believe</Link>
            </li>
            <li>
              <Link to="/give">Give</Link>
            </li>
            <li>
              <Link to="/contact">Contact</Link>
            </li>
            <li>
              <Link to="/login">Sign In</Link>
            </li>
          </ul>
        </div>
      </div>

      {/* Helps search engines and map results resolve the church correctly. */}
      <script
        type="application/ld+json"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Church',
            name: church.name,
            address: {
              '@type': 'PostalAddress',
              streetAddress: church.address.street,
              addressLocality: church.address.city,
              addressRegion: church.address.state,
              postalCode: church.address.zip,
              addressCountry: 'US',
            },
            telephone: church.phone,
            email: church.email,
            sameAs: church.social.map((s) => s.href),
          }),
        }}
      />
      <span className="sr-only">{formattedAddress}</span>
    </footer>
  );
}
